import * as pdfjs from "pdfjs-dist";
import type { PDFDocumentProxy } from "pdfjs-dist";

let workerConfigured = false;

/**
 * Stable path copied to `public/pdf.worker.min.mjs` by `scripts/copy-pdf-worker.mjs` (npm postinstall).
 * Avoids Vite-hashed `/assets/pdf.worker-*.mjs` URLs that can 404 or break module workers after deploys,
 * which triggers pdf.js fake-worker `import()` and the status-bar error the user saw.
 */
function pdfWorkerPublicUrl(): string {
  const base = import.meta.env.BASE_URL ?? "/";
  const path = `${base.replace(/\/?$/, "/")}pdf.worker.min.mjs`;
  if (typeof window === "undefined") return path;
  return new URL(path, window.location.href).href;
}

function ensurePdfWorker(): void {
  if (workerConfigured) return;
  pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerPublicUrl();
  workerConfigured = true;
}

/**
 * Parse page index from a Max PDF image `src`.
 * Prefer `;npp-page=N` in the MIME section — survives DOMPurify, which often strips `#page=N` fragments.
 * Legacy: `data:application/pdf;base64,…#page=N` (fragment may be lost after sanitize; still parsed when present).
 */
export function parsePdfDataUrlWithPage(src: string): { pdfDataUrlNoHash: string; page: number } | null {
  let full = src.trim();
  try {
    full = decodeURIComponent(full);
  } catch {
    /* keep */
  }
  if (!full.toLowerCase().startsWith("data:application/pdf")) return null;

  let page = 1;
  let withoutHash = full;
  const hashIdx = full.indexOf("#");
  if (hashIdx >= 0) {
    withoutHash = full.slice(0, hashIdx);
    const frag = full.slice(hashIdx + 1).trim();
    if (frag) {
      const pm = /^page=(\d{1,6})$/i.exec(frag);
      if (!pm) return null;
      page = Math.max(1, parseInt(pm[1]!, 10));
    }
  }

  const npp = /^data:application\/pdf(?:;npp-page=(\d{1,6}))?;base64,/i.exec(withoutHash);
  if (!npp) return null;
  if (npp[1]) {
    page = Math.max(1, parseInt(npp[1], 10));
  }

  const forDecode = withoutHash.replace(/;npp-page=\d{1,6}(?=;base64,)/i, "");
  if (!/^data:application\/pdf;base64,/i.test(forDecode)) return null;
  return { pdfDataUrlNoHash: forDecode, page };
}

/** Decode `data:application/pdf;base64,…` (no `#` / no `;npp-page=` — use {@link parsePdfDataUrlWithPage} first). */
export function decodePdfDataUrlToUint8Array(pdfDataUrlNoHash: string): Uint8Array | null {
  const m = /^data:application\/pdf;base64,([\s\S]+)$/i.exec(pdfDataUrlNoHash.trim());
  if (!m?.[1]) return null;
  try {
    const bin = atob(m[1].replace(/\s/g, ""));
    const len = bin.length;
    const out = new Uint8Array(len);
    for (let i = 0; i < len; i++) out[i] = bin.charCodeAt(i) & 0xff;
    return out;
  } catch {
    return null;
  }
}

/** Number of pages in a PDF file (reads the file once). */
export async function getPdfPageCountFromFile(file: File): Promise<number> {
  const data = new Uint8Array(await file.arrayBuffer());
  return getPdfPageCountFromBytes(data);
}

export async function getPdfPageCountFromBytes(data: Uint8Array): Promise<number> {
  ensurePdfWorker();
  const loadingTask = pdfjs.getDocument({ data, stopAtErrors: true });
  let pdf: PDFDocumentProxy | null = null;
  try {
    pdf = await loadingTask.promise;
    return pdf.numPages;
  } finally {
    if (pdf) await pdf.destroy().catch(() => {});
    await loadingTask.destroy().catch(() => {});
  }
}

/** Rasterize one PDF page (1-based) to a PNG data URL. */
export async function pdfBytesToPngDataUrl(
  data: Uint8Array,
  pageNumber1Based: number,
  scale = 1.35,
): Promise<string> {
  ensurePdfWorker();
  const loadingTask = pdfjs.getDocument({ data, stopAtErrors: true });
  let pdf: PDFDocumentProxy | null = null;
  try {
    pdf = await loadingTask.promise;
    const n = pdf.numPages;
    const p = Math.min(Math.max(1, Math.floor(pageNumber1Based)), n);
    const page = await pdf.getPage(p);
    const viewport = page.getViewport({ scale });
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas 2D context unavailable");
    canvas.width = Math.ceil(viewport.width);
    canvas.height = Math.ceil(viewport.height);
    const renderTask = page.render({ canvasContext: ctx, viewport });
    await renderTask.promise;
    await page.cleanup();
    return canvas.toDataURL("image/png");
  } finally {
    if (pdf) await pdf.destroy().catch(() => {});
    await loadingTask.destroy().catch(() => {});
  }
}

/**
 * Build `data:application/pdf;…;base64,…` from file bytes (single read).
 * When `page1Based` is set, adds `;npp-page=N` before `;base64,` so the page survives HTML sanitizers
 * that strip URL fragments.
 */
export async function readPdfFileAsDataUrl(file: File, page1Based?: number): Promise<string> {
  const buf = await file.arrayBuffer();
  const u8 = new Uint8Array(buf);
  let bin = "";
  const chunk = 0x8000;
  for (let i = 0; i < u8.length; i += chunk) {
    bin += String.fromCharCode.apply(null, u8.subarray(i, i + chunk) as unknown as number[]);
  }
  const b64 = btoa(bin);
  if (page1Based != null && Number.isFinite(page1Based)) {
    const p = Math.min(Math.max(1, Math.floor(page1Based)), 999_999);
    return `data:application/pdf;npp-page=${p};base64,${b64}`;
  }
  return `data:application/pdf;base64,${b64}`;
}

/**
 * Re-point a `data:application/pdf;base64,…` (or existing `;npp-page=`) URL at another 1-based page
 * without re-encoding bytes. Use after a single {@link readPdfFileAsDataUrl} read for multi-page inserts.
 */
export function pdfDataUrlWithPage(dataUrl: string, page1Based: number): string {
  const p = Math.min(Math.max(1, Math.floor(page1Based)), 999_999);
  const t = dataUrl.trim();
  const m = /^data:application\/pdf(?:;npp-page=\d{1,6})?;base64,(.+)$/is.exec(t);
  if (!m?.[1]) throw new Error("Expected data:application/pdf…;base64,…");
  return `data:application/pdf;npp-page=${p};base64,${m[1]}`;
}

/** Hard cap on how many PDF pages we insert in one Attach action (each line repeats the file’s base64). */
export const MAX_PDF_INSERT_PAGES_PER_ACTION = 72;

/**
 * Parse user input for “which pages” when inserting a PDF into Max.
 * Accepts `all`, comma-separated numbers, and ranges like `1-4` or `4-1` (order normalized).
 */
export function parsePdfInsertPageSelection(raw: string, numPages: number): number[] {
  if (numPages < 1) return [];
  const t = raw
    .trim()
    .toLowerCase()
    .replace(/\u2013/g, "-")
    .replace(/\s+/g, " ");
  if (!t) return [];
  if (t === "all" || t === "*") {
    return Array.from({ length: numPages }, (_, i) => i + 1);
  }
  const out = new Set<number>();
  const parts = t.split(",").map((s) => s.trim()).filter(Boolean);
  for (const part of parts) {
    const rangeM = /^(\d{1,6})\s*-\s*(\d{1,6})$/.exec(part);
    if (rangeM) {
      let a = parseInt(rangeM[1]!, 10);
      let b = parseInt(rangeM[2]!, 10);
      if (!Number.isFinite(a) || !Number.isFinite(b)) continue;
      if (a > b) [a, b] = [b, a];
      a = Math.max(1, Math.min(a, numPages));
      b = Math.max(1, Math.min(b, numPages));
      for (let q = a; q <= b; q++) out.add(q);
    } else if (/^\d{1,6}$/.test(part)) {
      const q = parseInt(part, 10);
      if (!Number.isFinite(q)) continue;
      out.add(Math.max(1, Math.min(q, numPages)));
    }
  }
  return Array.from(out).sort((a, b) => a - b);
}

/** LRU: repeated Max preview rebuilds reuse PNGs so `<img src=data:application/pdf>` is not left visible. */
const PDF_RASTER_PNG_CACHE_MAX = 48;
const pdfRasterPngCache = new Map<string, string>();

function pdfBytesFingerprintForRasterCache(bytes: Uint8Array): string {
  const n = bytes.length;
  let h = 2166136261 >>> 0;
  const stride = Math.max(1, Math.floor(n / 16384));
  for (let i = 0; i < n; i += stride) {
    h ^= bytes[i]!;
    h = Math.imul(h, 16777619) >>> 0;
  }
  const headLen = Math.min(48, n);
  let head = 0;
  for (let i = 0; i < headLen; i++) head = (head * 31 + bytes[i]!) >>> 0;
  const tail = n ? bytes[n - 1]! : 0;
  return `${n}:${h}:${head}:${tail}`;
}

function pdfRasterPngCacheKey(bytes: Uint8Array, page: number): string {
  return `${page}@${pdfBytesFingerprintForRasterCache(bytes)}`;
}

function peekPdfRasterPngCache(key: string): string | undefined {
  const png = pdfRasterPngCache.get(key);
  if (png === undefined) return undefined;
  pdfRasterPngCache.delete(key);
  pdfRasterPngCache.set(key, png);
  return png;
}

function putPdfRasterPngCache(key: string, png: string): void {
  pdfRasterPngCache.delete(key);
  pdfRasterPngCache.set(key, png);
  while (pdfRasterPngCache.size > PDF_RASTER_PNG_CACHE_MAX) {
    const first = pdfRasterPngCache.keys().next().value;
    if (first !== undefined) pdfRasterPngCache.delete(first);
  }
}

/**
 * Replace `<img src="data:application/pdf;…;base64,…">` (page from `;npp-page=N` or legacy `#page=N`)
 * with a PNG raster of that page. Uses an in-memory LRU so preview refreshes re-apply PNGs synchronously.
 */
export function mountMaxPdfRasterImages(root: HTMLElement): void {
  const imgs = root.querySelectorAll<HTMLImageElement>("img[src]");
  imgs.forEach((img) => {
    const src = img.getAttribute("src");
    if (!src?.startsWith("data:application/pdf")) return;
    const parsed = parsePdfDataUrlWithPage(src);
    if (!parsed) return;
    const bytes = decodePdfDataUrlToUint8Array(parsed.pdfDataUrlNoHash);
    if (!bytes?.length) return;
    const key = pdfRasterPngCacheKey(bytes, parsed.page);
    const cached = peekPdfRasterPngCache(key);
    if (cached) {
      img.src = cached;
      img.dataset.nppMaxPdfPage = String(parsed.page);
      return;
    }
    void pdfBytesToPngDataUrl(bytes, parsed.page, 1.35)
      .then((png) => {
        if (!img.isConnected) return;
        putPdfRasterPngCache(key, png);
        img.src = png;
        img.dataset.nppMaxPdfPage = String(parsed.page);
      })
      .catch(() => {
        if (!img.isConnected) return;
        img.alt = `PDF page ${parsed.page} (raster failed)`;
      });
  });
}
